/**
 * Path-definition helpers for `src/web/paths/` and `src/partnerApi/paths/`.
 *
 * Stable util — do not add domain-specific routes here. Domains only append PathDef entries
 * under their surface `paths/<domain>.ts`.
 */
import type { TSchema } from '@sinclair/typebox';
import { HtmlDocument, PdfDocument, ProblemDetails } from '../web/interfaces/common';

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export type ResponseDef = {
  description: string;
  schema?: TSchema;
  contentType?: string;
  /** Additional media types for the same status (e.g. PDF + HTML document download). */
  alternates?: Array<{ contentType: string; schema: TSchema }>;
};

/**
 * One HTTP operation. Request/response/query shapes are imported interface schemas —
 * field properties are never restated here.
 *
 * `handler` is optional so a path can be declared before the BFF route exists.
 * When set, it must match `domains/bff/sst.ts` and is used by `check:contracts`.
 */
export type PathDef = {
  method: HttpMethod;
  path: string;
  /** BFF Lambda export path, e.g. `domains/bff/src/health/index.handler`. */
  handler?: string;
  tags: string[];
  summary: string;
  description?: string;
  /**
   * `'bearerAuth'` → JWT required. `'public'` → emits OpenAPI `security: []`
   * (explicitly unauthenticated; required by Redocly `security-defined`).
   */
  security: 'bearerAuth' | 'public';
  /** Flat object schema; properties become OpenAPI query parameters. */
  query?: TSchema;
  /** Flat object schema; properties become OpenAPI header parameters. */
  headers?: TSchema;
  request?: TSchema;
  responses: Record<number, ResponseDef>;
};

export const json = (description: string, schema: TSchema): ResponseDef => ({
  description,
  schema,
});

/** Every error response in this API is `application/problem+json`. */
export const problem = (description: string): ResponseDef => ({
  description,
  schema: ProblemDetails,
  contentType: 'application/problem+json',
});

export const pdf = (description: string): ResponseDef => ({
  description,
  schema: PdfDocument,
  contentType: 'application/pdf',
});

export const html = (description: string): ResponseDef => ({
  description,
  schema: HtmlDocument,
  contentType: 'text/html',
});

/** Stored artifact download — PDF or HTML selected by query `format`. */
export const document = (description: string): ResponseDef => ({
  description,
  schema: PdfDocument,
  contentType: 'application/pdf',
  alternates: [{ contentType: 'text/html', schema: HtmlDocument }],
});

/** Empty success body (e.g. DELETE 204). */
export const empty = (description: string): ResponseDef => ({
  description,
});
